import {
  PLANNING_LENS_NEEDS_PADDOCK_MESSAGE,
  PLANNING_LENS_NEEDS_SCOPE_MESSAGE,
} from 'constants/messageConstants';
import {
  INPUT_DOC_LENS_NUTRITION,
  INPUT_DOC_LENS_ROTATION,
  PLANNING_LENS_PADDOCK_PLAN,
  PLANNING_LENS_PADDOCKS,
} from 'constants/uiConstants';

import type { InsightsScopeLevel } from '../Workspace/Insights/insightUtils';

// Kept out of useFarmsNavSync so it can be unit-tested without pulling in the
// service layer the hook's data fetching depends on.

export type PlanningLens =
  | typeof PLANNING_LENS_PADDOCK_PLAN
  | typeof INPUT_DOC_LENS_ROTATION
  | typeof INPUT_DOC_LENS_NUTRITION
  | typeof PLANNING_LENS_PADDOCKS;

/**
 * Every Planning lens, in the order the switcher always shows them — a lens
 * that doesn't apply to the tree selection is greyed out, never removed, so
 * the tools stay where users expect them (OP-2549).
 */
export const PLANNING_LENS_ORDER: PlanningLens[] = [
  INPUT_DOC_LENS_ROTATION,
  PLANNING_LENS_PADDOCK_PLAN,
  INPUT_DOC_LENS_NUTRITION,
  PLANNING_LENS_PADDOCKS,
];

/**
 * The Planning lenses available for a tree scope, default first: a plan or
 * paddock opens Paddock plan (a paddock opens its plan, or offers to choose
 * or add one), with Rotation (that paddock's years), Nutrition and Paddocks
 * (its farm, OP-2481) beside it; a farm or the organisation opens Rotation,
 * with Nutrition and Paddocks — Paddock plan needs one paddock. A crop node
 * has none.
 */
export const getPlanningLensOptions = (
  scopeLevel: InsightsScopeLevel | null | undefined
): PlanningLens[] => {
  switch (scopeLevel) {
    case 'input':
    case 'paddock':
      return [
        PLANNING_LENS_PADDOCK_PLAN,
        INPUT_DOC_LENS_ROTATION,
        INPUT_DOC_LENS_NUTRITION,
        PLANNING_LENS_PADDOCKS,
      ];
    case 'farm':
    case 'org':
      return [
        INPUT_DOC_LENS_ROTATION,
        INPUT_DOC_LENS_NUTRITION,
        PLANNING_LENS_PADDOCKS,
      ];
    default:
      return [];
  }
};

/**
 * Why a lens is greyed out for a scope, for its tooltip — or null when it's
 * available.
 */
export const getPlanningLensUnavailableReason = (
  lens: PlanningLens,
  scopeLevel: InsightsScopeLevel | null | undefined
): string | null => {
  if (getPlanningLensOptions(scopeLevel).includes(lens)) return null;
  return lens === PLANNING_LENS_PADDOCK_PLAN &&
    (scopeLevel === 'farm' || scopeLevel === 'org')
    ? PLANNING_LENS_NEEDS_PADDOCK_MESSAGE
    : PLANNING_LENS_NEEDS_SCOPE_MESSAGE;
};

/**
 * The `?plens=` URL param resolved for a scope: the named lens when it's
 * offered there, otherwise the scope's default — so a lens never shows where
 * it doesn't apply. Null when the scope has no Planning lens.
 */
export const resolvePlanningLens = (
  param: string | null,
  scopeLevel: InsightsScopeLevel | null | undefined
): PlanningLens | null => {
  const options = getPlanningLensOptions(scopeLevel);
  const named = options.find((lens) => lens === param);
  return named ?? options[0] ?? null;
};

/**
 * Nutrition and Rotation moved from Insights to Planning (OP-2549). An old
 * `?tab=insights&lens=…` link to either names the Planning lens it now opens;
 * null for any other link.
 */
export const resolveMovedInsightsLens = (
  tabParam: string | null,
  lensParam: string | null,
  insightsTab: string
): PlanningLens | null =>
  tabParam === insightsTab &&
  (lensParam === INPUT_DOC_LENS_NUTRITION ||
    lensParam === INPUT_DOC_LENS_ROTATION)
    ? lensParam
    : null;
